/**
 * frontend/src/components/ChatModal.jsx
 *
 * User ↔ admin chat thread. Replaces old FeedbackModal.
 * - Message bubbles (user right teal, admin left slate)
 * - Polls /api/chat/thread every 10s while open for new admin replies
 * - Send via POST /api/chat/send (query param)
 * - Auto-marks admin replies read on open (GET /chat/thread side effect)
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL = 10000; // 10s

export function ChatModal({ open, onClose }) {
  const { user, token } = useAuth();
  const { refreshChatUnread } = useNotifications();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft]       = useState('');
  const [sending, setSending]   = useState(false);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const scrollRef = useRef(null);
  const pollRef   = useRef(null);

  const authHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }), [token]);

  const fetchThread = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/chat/thread`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMessages(data.messages || []);
      setError('');
    } catch (err) {
      setError('ჩატვირთვის შეცდომა');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  // Initial load + polling while open
  useEffect(() => {
    if (!open || !token) return;
    setLoading(true);
    fetchThread();
    pollRef.current = setInterval(fetchThread, POLL_INTERVAL);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [open, token, fetchThread]);

  // Refresh global unread badge when modal closes (admin replies marked read)
  useEffect(() => {
    if (!open && refreshChatUnread) refreshChatUnread();
  }, [open, refreshChatUnread]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  // ESC closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock body scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  async function send(e) {
    e.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setError('');
    try {
      const params = new URLSearchParams({ text });
      const res = await fetch(`${API}/api/chat/send?${params}`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'შეცდომა');
      }
      setDraft('');
      await fetchThread();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  }

  // Not logged in → prompt
  if (!user) {
    return (
      <div
        className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-black/50"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 text-center space-y-3"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="text-base font-semibold text-slate-800">ჩატი ადმინთან</h2>
          <p className="text-sm text-slate-600">
            ჩატის გასახსნელად საჭიროა შესვლა.
          </p>
          <button
            onClick={onClose}
            className="mt-3 px-6 py-2 bg-teal-500 hover:bg-teal-600 text-white text-sm rounded-lg"
          >
            დახურვა
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md flex flex-col"
        style={{ height: '80vh', maxHeight: 600 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 shrink-0">
          <div>
            <h2 className="text-base font-semibold text-slate-800">ჩატი ადმინთან</h2>
            <p className="text-[11px] text-slate-500">სუპორტი დაგიკავშირდებათ მალე</p>
          </div>
          <button
            onClick={onClose}
            aria-label="დახურვა"
            className="p-1 hover:bg-slate-100 rounded-lg"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-slate-50"
        >
          {loading ? (
            <div className="text-center text-xs text-slate-400 py-8">
              ჩატვირთვა...
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-xs text-slate-500 py-8 space-y-1">
              <p>ჯერ შეტყობინება არ გაგზავნილა.</p>
              <p className="text-slate-400">დაგვიწერეთ ნებისმიერი საკითხი ან შენიშვნა.</p>
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.sender === 'user';
              return (
                <div
                  key={m._id}
                  className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                      mine
                        ? 'bg-teal-500 text-white rounded-br-sm'
                        : 'bg-white text-slate-800 rounded-bl-sm shadow-sm'
                    }`}
                  >
                    <p className="whitespace-pre-wrap break-words">{m.text}</p>
                    <p
                      className={`text-[10px] mt-1 ${
                        mine ? 'text-teal-100' : 'text-slate-400'
                      }`}
                    >
                      {formatTime(m.created_at)}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 px-4 py-2 bg-red-50 border-t border-red-100 shrink-0">
            <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
            <p className="text-xs text-red-600">{error}</p>
          </div>
        )}

        {/* Composer */}
        <form
          onSubmit={send}
          className="flex items-end gap-2 px-3 py-2 border-t border-slate-100 shrink-0 bg-white"
        >
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send(e);
              }
            }}
            rows={1}
            maxLength={2000}
            placeholder="დაწერეთ შეტყობინება..."
            className="flex-1 resize-none px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400 max-h-32"
          />
          <button
            type="submit"
            disabled={!draft.trim() || sending}
            className="p-2.5 bg-teal-500 hover:bg-teal-600 disabled:opacity-40 text-white rounded-xl shrink-0"
            aria-label="გაგზავნა"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('ka-GE', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

export default ChatModal;
